import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { SHOPIFY_PROJECTS } from "../../data/site";

const FallbackTile = ({ name }) => (
    <div className="relative h-full w-full bg-gradient-to-br from-brand-dark via-brand to-blue-400 transition-transform duration-700 ease-out group-hover:scale-[1.05]">
        <div
            className="absolute inset-0 opacity-20"
            style={{
                backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)",
                backgroundSize: "40px 40px",
            }}
            aria-hidden="true"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-6xl font-bold text-white/95">{name.charAt(0)}</span>
            <span className="mt-2 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-100">
                {name}
            </span>
        </div>
    </div>
);

const ProjectCard = ({ project, index }) => {
    const openProject = () => {
        if (project.url) {
            window.open(project.url, "_blank", "noopener,noreferrer");
        } else {
            toast.info(project.name, {
                description: "Live link will be added here — ask us about this project.",
            });
        }
    };

    return (
        <Reveal delay={(index % 3) * 0.08}>
            <article
                data-testid={`shopify-project-${project.num}`}
                className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-brand/40 hover:shadow-hover"
            >
                <button
                    type="button"
                    onClick={openProject}
                    data-testid={`shopify-project-image-${project.num}`}
                    className="relative block aspect-[16/10] w-full cursor-pointer overflow-hidden bg-slate-100 text-left"
                    aria-label={`View ${project.name}`}
                >
                    {project.image ? (
                        <img
                            src={project.image}
                            alt={`${project.name} — Shopify store`}
                            loading="lazy"
                            className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                        />
                    ) : (
                        <FallbackTile name={project.name} />
                    )}
                    <div
                        className="absolute inset-0 bg-gradient-to-t from-brand-dark/70 via-brand-dark/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                        aria-hidden="true"
                    />
                    <span
                        className="absolute bottom-4 right-4 flex h-11 w-11 translate-y-3 items-center justify-center rounded-full bg-white text-brand-dark opacity-0 shadow-hover transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100"
                        aria-hidden="true"
                    >
                        <ArrowUpRight className="h-5 w-5" />
                    </span>
                    <span className="absolute left-4 top-4 rounded-full border border-white/40 bg-white/85 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600 backdrop-blur-md">
                        {project.num}
                    </span>
                </button>
                <div className="p-6">
                    <h3 className="font-display text-xl font-semibold tracking-tight text-slate-900">
                        {project.name}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{project.desc}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                        {project.tags.map((t) => (
                            <span
                                key={t}
                                className="rounded-full border border-brand/20 bg-brand-subtle px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-brand-dark"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                    <button
                        type="button"
                        data-testid={`shopify-project-link-${project.num}`}
                        onClick={openProject}
                        className="group/btn mt-5 flex items-center gap-1.5 text-sm font-semibold text-brand transition-colors duration-300 hover:text-brand-dark"
                    >
                        View featured work
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                    </button>
                </div>
            </article>
        </Reveal>
    );
};

export default function ShopifyWork() {
    return (
        <section data-testid="shopify-work-section" className="relative border-t border-slate-200 bg-white py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <Reveal className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <Eyebrow testId="shopify-work-eyebrow">Shopify projects</Eyebrow>
                        <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                            Featured work
                        </h2>
                    </div>
                    <div className="max-w-md">
                        <img
                            src="/brands/shopify-select-partner.png"
                            alt="Shopify Select Partner"
                            loading="lazy"
                            data-testid="shopify-partner-badge"
                            className="mb-4 h-14 w-auto"
                        />
                        <p className="text-sm leading-relaxed text-slate-500 sm:text-base">
                            A selection of Shopify stores we've designed, built and optimised for
                            brands across markets.
                        </p>
                    </div>
                </Reveal>
                <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
                    {SHOPIFY_PROJECTS.map((p, i) => (
                        <ProjectCard key={p.num} project={p} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
